import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

interface User {
  id: string;
  email: string;
  picture: string;
  roles: string[];
  firstName: string;
  lastName: string;
}

interface SessionContextType {
  token: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  user: User | null;
  roles: string[];
  hasRole: (role: string | string[]) => boolean;
  logout: () => void;
  login: () => void;
}

type JwtPayload = Record<string, string | string[] | undefined | null>;

const AUTH0_NAMESPACE = import.meta.env.VITE_AUTH0_NAMESPACE;
const VALID_ROLES = ["ADMIN"];

const SessionContext = createContext<SessionContextType | undefined>(undefined);

// Add fetchUser function
const fetchUser = async (userId: string, token: string) => {
  const response = await axios.get<User>(
    `${import.meta.env.VITE_API_URL}/api/v2/users/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<JwtPayload | null>(null);
  const logout = useCallback(
    () => auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
    [auth0Logout]
  );

  // Update token fetching effect
  useEffect(() => {
    if (isAuth0Loading) {
      return;
    }

    if (!isAuthenticated) {
      setIsLoading(false);
      setToken(null);
      setDecodedToken(null);
      return;
    }

    const getToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decoded = jwtDecode<JwtPayload>(token);
        setToken(token);
        setDecodedToken(decoded);
      } catch (error) {
        console.error("Error getting token:", error);
        void logout();
      } finally {
        setIsLoading(false);
      }
    };

    void getToken();
  }, [isAuthenticated, getAccessTokenSilently, isAuth0Loading, logout]);

  // Add memoized token data
  const { roles, userId, isUserValid } = useMemo(() => {
    const userId = decodedToken?.[
      `${AUTH0_NAMESPACE}/internalUserId`
    ] as string;
    const roles =
      (decodedToken?.[`${AUTH0_NAMESPACE}/roles`] as string[]) ?? [];
    const isUserValid = roles.some((role) => VALID_ROLES.includes(role));

    return { roles, userId, isUserValid };
  }, [decodedToken]);

  // Add React Query for user data
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    queryKey: ["user", userId, token],
    // Guarded by `enabled` below; both userId and token are non-null when query runs.
    queryFn: () => fetchUser(userId, token!),
    enabled: !!userId && !!token && isUserValid,
  });

  // Handle errors
  useEffect(() => {
    if (userError) {
      console.error("Session error:", { userError });
      void logout();
    }
  }, [userError, logout]);

  const hasRole = useCallback(
    (requiredRoles: string | string[]) => {
      const roleArray = Array.isArray(requiredRoles)
        ? requiredRoles
        : [requiredRoles];
      return roleArray.some((role) => roles.includes(role));
    },
    [roles]
  );

  const isInitializing = isAuth0Loading || isLoading || isUserLoading;

  return (
    <SessionContext.Provider
      value={{
        isLoading: isInitializing,
        isLoggedIn: isAuthenticated && !!token,
        user: user ?? null,
        roles,
        token,
        hasRole,
        login: () => void loginWithRedirect(),
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
